import { httpClient } from '@bs/core'
import type {
  Tag,
  TagDetailResponse,
  TagFormValues,
  TagListParams,
  TagListResponse,
  TagSlugHistoryEntry,
} from '../../types/tag'

const TAGS_URL = '/stores/woocommerce/products/tags/'

// Mirror of frontend/libs/pantry-features/src/gateways/breadstack/categoryApis.ts:170-176
// toSlug(). Duplicated locally because pantry-features cannot import across
// feature modules. Backend requires a slug that starts with alphanumerics and
// contains only alphanumerics / hyphens.
function toSlug(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
}

const TAG_COLOR_IDS = [
  'brand',
  'teal',
  'blue',
  'violet',
  'pink',
  'red',
  'orange',
  'amber',
  'lime',
  'slate',
  'rose',
  'indigo',
]

const getDefaultColorId = (name: string, id: number): string => {
  const index = (name.charCodeAt(0) + id) % TAG_COLOR_IDS.length
  return TAG_COLOR_IDS[index] ?? TAG_COLOR_IDS[0]
}

interface ApiTagItem {
  id: number
  name: string
  slug?: string
  description?: string
  count?: number
  visible?: boolean
  color?: string
  badge_style?: string
  store_id?: string
  slug_locked?: boolean
  redirect_count?: number
}

interface ApiTagSlugHistoryEntry {
  id?: string
  slug?: string
  created_at?: string
}

interface ApiTagDetail extends ApiTagItem {
  slug_history?: ApiTagSlugHistoryEntry[]
}

interface PaginateResponse {
  items?: ApiTagItem[]
  total_items?: number
  total_pages?: number
  page?: number
  // Pantry-only fields — absent for legacy WooCommerce stores. The mapper
  // defaults them to 0 so the response shape is uniform on the frontend.
  visible_count?: number
  hidden_count?: number
}

function mapFromApi(item: ApiTagItem): Tag {
  const colorId = TAG_COLOR_IDS.includes(item.color ?? '')
    ? (item.color as string)
    : getDefaultColorId(item.name ?? '', item.id)

  const badgeStyle =
    item.badge_style === 'outline' || item.badge_style === 'subtle'
      ? (item.badge_style as 'outline' | 'subtle')
      : 'filled'

  return {
    id: item.id,
    name: item.name ?? '',
    slug: item.slug ?? '',
    description: item.description ?? '',
    count: Number(item.count ?? 0),
    visible: item.visible ?? true,
    colorId,
    style: badgeStyle,
    slugLocked: item.slug_locked ?? false,
    redirectCount:
      typeof item.redirect_count === 'number' ? item.redirect_count : undefined,
  }
}

function mapSlugHistoryFromApi(
  entry: ApiTagSlugHistoryEntry,
): TagSlugHistoryEntry {
  return {
    id: entry.id ?? '',
    slug: entry.slug ?? '',
    createdAt: entry.created_at,
  }
}

export interface TagItem {
  id: number
  name: string
}

export const tagApis = {
  getList: async (): Promise<TagItem[]> => {
    const raw = (await httpClient.json.post(TAGS_URL, {
      action: 'list_all',
      parameters: {},
    })) as { data: ApiTagItem[] } | ApiTagItem[] | null
    if (!raw) return []
    const items = Array.isArray(raw)
      ? raw
      : ((raw as { data?: ApiTagItem[] }).data ?? [])
    return items.map((item) => ({ id: item.id, name: item.name }))
  },

  paginate: async (
    storeId: string,
    params: Partial<TagListParams>,
  ): Promise<TagListResponse> => {
    const { sort_by, sort, ...rest } = params
    const sortParam = sort_by ? { sort: { [sort_by]: Number(sort) } } : {}
    const cleanParams = Object.fromEntries(
      Object.entries({ ...rest, ...sortParam }).filter(
        ([, v]) => v !== null && v !== undefined && v !== '',
      ),
    )
    const raw = (await httpClient.json.post(TAGS_URL, {
      store_id: storeId,
      action: 'paginate',
      parameters: cleanParams,
    })) as PaginateResponse

    const items = raw?.items ?? []
    return {
      data: items.map(mapFromApi),
      totalItems: raw?.total_items ?? 0,
      totalPages: raw?.total_pages ?? 0,
      currentPage: raw?.page ?? 1,
      visibleCount: raw?.visible_count ?? 0,
      hiddenCount: raw?.hidden_count ?? 0,
    }
  },

  // POSTs `action: 'read'` to fetch the full tag including `slug_history` (for
  // the active 301 redirects chip section in the drawer). Backend attaches
  // `slug_history` only for Pantry stores; legacy stores get no field. Used
  // by `useTagDetail` to hydrate the drawer in edit mode.
  getDetail: async (
    storeId: string,
    tagId: number,
  ): Promise<TagDetailResponse | null> => {
    const raw = (await httpClient.json.post(TAGS_URL, {
      store_id: storeId,
      action: 'read',
      parameters: { id: tagId },
    })) as ApiTagDetail | { data?: ApiTagDetail } | null
    if (!raw) return null
    const item: ApiTagDetail | null =
      (raw as { data?: ApiTagDetail }).data ??
      ((raw as ApiTagDetail).id !== undefined ? (raw as ApiTagDetail) : null)
    if (!item) return null
    const tag = mapFromApi(item) as TagDetailResponse
    if (Array.isArray(item.slug_history)) {
      tag.slugHistory = item.slug_history.map(mapSlugHistoryFromApi)
    }
    return tag
  },

  create: async (storeId: string, values: TagFormValues): Promise<void> => {
    await httpClient.json.post(TAGS_URL, {
      store_id: storeId,
      action: 'create',
      parameters: {
        item_data: {
          name: values.name.trim(),
          slug: values.slug.trim() ? toSlug(values.slug) : '',
          description: values.description,
          color: values.colorId,
          badge_style: values.style,
          visible: values.visible,
          slug_locked: values.slugLocked ?? false,
        },
      },
    })
  },

  update: async (storeId: string, values: TagFormValues): Promise<void> => {
    // `slug_locked` is sent only on update when the caller passes a defined
    // value. The UpdateProductTagSerializer purposefully has NO default for
    // this field so partial updates (e.g. visibility toggle on the row) do
    // not silently flip an existing lock to false.
    const itemData: Record<string, unknown> = {
      id: values.id,
      name: values.name.trim(),
      slug: values.slug.trim() ? toSlug(values.slug) : '',
      description: values.description,
      color: values.colorId,
      badge_style: values.style,
      visible: values.visible,
    }
    if (typeof values.slugLocked === 'boolean') {
      itemData.slug_locked = values.slugLocked
    }
    await httpClient.json.post(TAGS_URL, {
      store_id: storeId,
      action: 'update',
      parameters: { item_data: itemData },
    })
  },

  updateVisibility: async (
    storeId: string,
    tag: Tag,
    visible: boolean,
  ): Promise<void> => {
    // Intentionally does NOT send `slug_locked` — the serializer omits the
    // default so this lightweight row-level toggle preserves whatever lock
    // state already exists on the doc.
    await httpClient.json.post(TAGS_URL, {
      store_id: storeId,
      action: 'update',
      parameters: {
        item_data: {
          id: tag.id,
          name: tag.name,
          slug: tag.slug,
          description: tag.description,
          visible,
        },
      },
    })
  },

  deleteOne: async (storeId: string, id: number): Promise<void> => {
    await httpClient.json.post(TAGS_URL, {
      store_id: storeId,
      action: 'delete',
      parameters: { id },
    })
  },

  deleteBulk: async (storeId: string, ids: number[]): Promise<void> => {
    await httpClient.json.post(TAGS_URL, {
      store_id: storeId,
      action: 'batch_update',
      parameters: { data: { delete: ids } },
    })
  },

  // DELETE /stores/:storeId/tags/:tagId/slug-history/:slugHistoryId
  // Removes one active 301 redirect entry. Backend refuses to delete the row
  // that mirrors the tag's current slug (it's operational scaffolding for the
  // next slug rename) and returns 400 in that case. `slugHistoryId` is the
  // Mongo `_id` stringified (matches TagSlugHistoryEntry.id).
  deleteSlugHistory: async (
    storeId: string,
    tagId: number,
    slugHistoryId: string,
  ): Promise<unknown> => {
    return httpClient.delete(
      `/stores/${storeId}/tags/${tagId}/slug-history/${slugHistoryId}`,
    )
  },
}
